import { Component, Input, OnChanges, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { DeviceFilter } from 'DiscoveryEngine.TS/Core';
import { ViewerDeviceFilterDto, Owner } from 'UvExplorerServer.Dtos.TS/Viewer';
import { NetViewTreeNodeDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { Changeable } from 'common';
import { DeviceFilterComponent } from '../device-filter/device-filter.component';
import { LicensingService } from 'identity';


enum NavType { None, SelectFilter, AddFilter, DeleteFilter };


@Component({
	selector: 'device-filter-list',
	templateUrl: './device-filter-list.component.html',
	styleUrls: ['./device-filter-list.component.scss']
})
export class DeviceFilterListComponent implements OnChanges, AfterViewInit, Changeable {

	@Input() public target: NetViewTreeNodeDto;
	public get hasTarget(): boolean {
		return !!this.target;
	}

    @ViewChild(DeviceFilterComponent, { static: true })
	private filterEditor: DeviceFilterComponent;

	public selectedFilter: ViewerDeviceFilterDto;

	public allFilters: ViewerDeviceFilterDto[];

	constructor(private licensingService: LicensingService) {
		this.target = null;
		this.resetSelectedFilter();
		this.rebuildFilterList();
	}

	private onAppliedChange(): void {
		if (this.hasTarget) {
			this.target.HasChanges = true;
			this.sortFilterList();
		}
	}

	public ngAfterViewInit(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.resetSelectedFilter();
		this.rebuildFilterList();
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	private rebuildFilterList(): void {
		this.allFilters = [];

		if (this.hasTarget) {
			this.allFilters = this.target.DeviceFilters.ToArray();
			this.sortFilterList();
		}
	}

	private sortFilterList(): void {
		this.allFilters.sort((a, b) => a.DeviceFilter.Name.toLowerCase() > b.DeviceFilter.Name.toLowerCase() ? 1 : -1);
	}

	private navType: NavType = NavType.None;
	private navFilter: ViewerDeviceFilterDto = null;
	public showTempChangesDialog: boolean = false;

	private resetNavState(): void {
		this.navType = NavType.None;
		this.navFilter = null;
		this.showTempChangesDialog = false;
	}

    // Used in view
	private onSelectFilter(navFilter: ViewerDeviceFilterDto): void {
		if (this.filterEditor && this.filterEditor.tempChanges) {
			this.navType = NavType.SelectFilter;
			this.navFilter = navFilter;
			this.showTempChangesDialog = true;
			return;
		}

		this.selectFilter(navFilter);
	}

	public onAddFilter(): void {
		if (this.filterEditor && this.filterEditor.tempChanges) {
			this.navType = NavType.AddFilter;
			this.navFilter = null;
			this.showTempChangesDialog = true;
			return;
		}

		this.addFilter();
	}

    // Used in view
	private onDeleteFilter(navFilter: ViewerDeviceFilterDto): void {
		// If the currently-selected filter is being deleted,
		// there's no need to check for temp changes
		if (navFilter != this.selectedFilter) {
			if (this.filterEditor && this.filterEditor.tempChanges) {
				this.navType = NavType.DeleteFilter;
				this.navFilter = navFilter;
				this.showTempChangesDialog = true;
				return;
			}
		}

		this.deleteFilter(navFilter);
	}

	public onDiscardTempChanges(): void {
		this.filterEditor.discardTempChanges();
		this.completeNavigation();
		this.resetNavState();
	}

	public get applyTempChangesEnabled(): boolean {
		return (this.filterEditor && this.filterEditor.tempChangesValid);
	}

	private onApplyTempChanges(): void {
		this.filterEditor.applyTempChanges();
		this.completeNavigation();
		this.resetNavState();
	}

	public onCancelNavigation(): void {
		this.resetNavState();
	}

	private completeNavigation(): void {
		switch (this.navType) {
			case NavType.SelectFilter:
				this.selectFilter(this.navFilter);
				break;
			case NavType.AddFilter:
				this.addFilter();
				break;
			case NavType.DeleteFilter:
				this.deleteFilter(this.navFilter);
				break;
			default:
				throw new Error("Invalid navType");
		}
	}

	private addFilter(): void {

		if (this.hasTarget) {
			let df = new DeviceFilter();
			df.Name = "New Device Filter";
			df.VerifyGuid();

			let newFilter = new ViewerDeviceFilterDto(Owner.User, df);
			this.allFilters = [...this.allFilters, newFilter];
			this.selectFilter(newFilter);

			this.target.DeviceFilters.Add(newFilter);

			this.onAppliedChange();
		}
	}

	private deleteFilter(filter: ViewerDeviceFilterDto): void {

		let index = this.allFilters.indexOf(filter);
		if (index >= 0) {
			this.allFilters.splice(index, 1);
			if (this.selectedFilter === filter)
				this.resetSelectedFilter();

			this.target.DeviceFilters.Remove(filter);

			this.onAppliedChange();
		}
	}

	private selectFilter(filter: ViewerDeviceFilterDto): void {
		this.selectedFilter = filter;
	}

	private resetSelectedFilter(): void {
		this.selectedFilter = null;
	}

	//
	// Changeable
	//

	public get tempChanges(): boolean {
		return (this.filterEditor && this.filterEditor.tempChanges);
	}

	// PRE: tempChanges === true
	public get tempChangesValid(): boolean {
		if (this.filterEditor && this.filterEditor.tempChanges) {
			return this.filterEditor.tempChangesValid;
		}
		else {
			console.assert(false, "tempChangesValid called in invalid state");
			return false;
		}
	}

	// PRE: tempChanges === true && tempChangesValid === false
	public get invalidTempChanges(): string[] {
		if (this.filterEditor && this.filterEditor.tempChanges && !this.filterEditor.tempChangesValid) {
			return ["Device Filters"];
		}
		else {
			console.assert(false, "invalidTempChanges called in invalid state");
			return [];
		}
	}

	// PRE: tempChanges === true && tempChangesValid === true
	public applyTempChanges(): void {
		if (this.filterEditor && this.filterEditor.tempChanges && this.filterEditor.tempChangesValid) {
			this.filterEditor.applyTempChanges();
		}
		else {
			console.assert(false, "applyTempChanges called in invalid state");
		}
	}

	// PRE: tempChanges === true
	public discardTempChanges(): void {
		if (this.filterEditor && this.filterEditor.tempChanges) {
			this.filterEditor.discardTempChanges();
			//this.changeDetector.markForCheck();
		}
		else {
			console.assert(false, "discardTempChanges called in invalid state");
		}
	}

}
